/**
 * useChatModulePaneActions — the (non-entangled) module-pane focus/persist action
 * sub-layer lifted out of ChatBox (delegation pattern). dispatchModuleCommand,
 * focusModule, the focusModuleRef sync, hideModulePane, and the
 * pending-attached-module focus-apply effect are reproduced VERBATIM: same
 * bodies, same byte-identical dep arrays.
 *
 * SCOPE NOTE: only the pieces that do NOT depend on later-declared hooks
 * (applyUiActions/onCardAction/openWorkspaceModuleByLabel reference `onboarding`
 * and `useChat` outputs created further down) are lifted here, so this hook is a
 * CONTIGUOUS slice that stays at the SAME body position (before useChat) and
 * preserves hook order. focusModuleRef is created here and returned so the
 * orchestrator's applyUiActions / route-focus effects read the same stable
 * identity. Refs + setters thread in by reference; persistModulePaneState (from
 * useModulePaneState) threads in.
 */
import { useCallback, useEffect, useRef } from 'react';
import type { Dispatch, MutableRefObject, RefObject, SetStateAction } from 'react';
import { dispatchWindowEvent, conversationEvents } from '@/lib/conversation-events';
import type { Conversation } from '@/types/conversations';
import type { Module } from '@/types/modules';
import type { ModulePaneStatePaneState, PendingModuleFocus } from '@/features/chat/hooks/useModulePaneState';

export interface UseChatModulePaneActionsDeps {
  activeConversation: Conversation | null;
  isMobile: boolean;
  isEmbeddedModulePaneEnabled: boolean;
  isModulePaneVisible: boolean;
  chatPaneModuleId: string | null;
  selectedChatPaneModule: Module | null;
  availableModules: Module[];
  persistModulePaneState: (
    conversationId: string,
    moduleId: string,
    paneState: ModulePaneStatePaneState,
  ) => Promise<void>;
  setModuleEmbedAnchorMessageId: Dispatch<SetStateAction<string | null>>;
  setIsModulePaneVisible: (visible: boolean) => void;
  setChatPaneModuleId: Dispatch<SetStateAction<string | null>>;
  setMobileView: Dispatch<SetStateAction<'chat' | 'module'>>;
  latestMessageIdRef: MutableRefObject<string | null>;
  moduleEmbedAnchorByConversationRef: MutableRefObject<Record<string, string | null>>;
  modulePaneCardRef: RefObject<HTMLDivElement | null>;
  wasModulePaneSplitRef: MutableRefObject<boolean>;
  pendingAttachedModuleFocusQueueRef: MutableRefObject<PendingModuleFocus[]>;
  lastAppliedModuleFocusSequenceRef: MutableRefObject<number>;
}

export interface UseChatModulePaneActionsResult {
  dispatchModuleCommand: (detail: Record<string, unknown>) => void;
  focusModule: (
    moduleId: string,
    options?: { anchorMessageId?: string | null; shouldScrollIntoView?: boolean },
  ) => void;
  focusModuleRef: MutableRefObject<UseChatModulePaneActionsResult['focusModule']>;
  hideModulePane: () => void;
}

export function useChatModulePaneActions(deps: UseChatModulePaneActionsDeps): UseChatModulePaneActionsResult {
  const {
    activeConversation,
    isMobile,
    isEmbeddedModulePaneEnabled,
    isModulePaneVisible,
    chatPaneModuleId,
    selectedChatPaneModule,
    availableModules,
    persistModulePaneState,
    setModuleEmbedAnchorMessageId,
    setIsModulePaneVisible,
    setChatPaneModuleId,
    setMobileView,
    latestMessageIdRef,
    moduleEmbedAnchorByConversationRef,
    modulePaneCardRef,
    wasModulePaneSplitRef,
    pendingAttachedModuleFocusQueueRef,
    lastAppliedModuleFocusSequenceRef,
  } = deps;

  const dispatchModuleCommand = useCallback((detail: Record<string, unknown>) => {
    dispatchWindowEvent(conversationEvents.command, {
      ...detail,
      source: 'chatbox',
    });
  }, []);

  const focusModule = useCallback((
    moduleId: string,
    options?: { anchorMessageId?: string | null; shouldScrollIntoView?: boolean },
  ) => {
    const anchorAtOpen = options?.anchorMessageId ?? latestMessageIdRef.current;
    setModuleEmbedAnchorMessageId(anchorAtOpen);
    if (activeConversation?.id) {
      moduleEmbedAnchorByConversationRef.current[activeConversation.id] = anchorAtOpen;
    }
    setIsModulePaneVisible(true);
    setChatPaneModuleId(moduleId);
    // Switch to module view on mobile so the module is visible
    if (isMobile) setMobileView('module');
    if (activeConversation?.id) {
      void persistModulePaneState(activeConversation.id, moduleId, {
        isVisible: true,
        isFullscreen: true,
        anchorMessageId: anchorAtOpen,
      });
    }
    dispatchModuleCommand({
      action: 'focusModule',
      moduleId,
    });
    dispatchModuleCommand({
      action: 'updateModuleConfig',
      moduleId,
      config: { fullscreen: true },
    });
    // Keep sidebar "Recent modules" in sync for chat-driven opens/focus changes.
    if (activeConversation?.id) {
      dispatchWindowEvent(conversationEvents.moduleFocused, {
        conversationId: activeConversation.id,
        moduleId,
      });
    }
    if (isEmbeddedModulePaneEnabled && options?.shouldScrollIntoView !== false) {
      requestAnimationFrame(() => {
        modulePaneCardRef.current?.scrollIntoView({ behavior: 'auto', block: 'nearest' });
      });
    }
  }, [
    activeConversation?.id,
    dispatchModuleCommand,
    isMobile,
    isEmbeddedModulePaneEnabled,
    persistModulePaneState,
  ]);

  // Stable ref for focusModule so effects don't re-fire when it's recreated
  const focusModuleRef = useRef(focusModule);
  focusModuleRef.current = focusModule;

  const hideModulePane = useCallback(() => {
    const moduleId = chatPaneModuleId;
    const wasSplit = !isMobile && isModulePaneVisible && Boolean(selectedChatPaneModule);
    if (wasSplit) {
      dispatchWindowEvent(conversationEvents.setSidebarCollapsed, { collapsed: false });
      wasModulePaneSplitRef.current = false;
    }
    setIsModulePaneVisible(false);
    setMobileView('chat');
    if (activeConversation?.id && moduleId) {
      void persistModulePaneState(activeConversation.id, moduleId, {
        isVisible: false,
        isFullscreen: false,
        anchorMessageId: null,
      });
    }
    // Notify sidebar to clear active module highlight
    window.dispatchEvent(new CustomEvent('xavior:module-pane-closed'));
  }, [
    activeConversation?.id,
    chatPaneModuleId,
    isMobile,
    isModulePaneVisible,
    persistModulePaneState,
    selectedChatPaneModule,
  ]);

  // Close-from-sidebar command: the rail dispatches this when the user clicks
  // the open module's rail item, dropping the pane back to chat-only.
  useEffect(() => {
    const handleClose = () => hideModulePane();
    window.addEventListener(conversationEvents.closeModulePane, handleClose);
    return () => window.removeEventListener(conversationEvents.closeModulePane, handleClose);
  }, [hideModulePane]);

  useEffect(() => {
    if (!activeConversation?.id) return;
    const pendingQueue = pendingAttachedModuleFocusQueueRef.current;
    if (pendingQueue.length === 0) return;

    let latestReadyFocus: {
      moduleId: string;
      anchorMessageId: string | null;
      sequence: number;
    } | null = null;
    const remainingQueue: typeof pendingQueue = [];

    for (const pendingFocus of pendingQueue) {
      if (pendingFocus.conversationId !== activeConversation.id) continue;
      if (pendingFocus.sequence <= lastAppliedModuleFocusSequenceRef.current) continue;

      const existsNow = availableModules.some((module) => module.id === pendingFocus.moduleId);
      if (!existsNow) {
        remainingQueue.push(pendingFocus);
        continue;
      }

      if (!latestReadyFocus || pendingFocus.sequence > latestReadyFocus.sequence) {
        latestReadyFocus = pendingFocus;
      }
    }

    pendingAttachedModuleFocusQueueRef.current = remainingQueue;
    if (!latestReadyFocus) return;

    lastAppliedModuleFocusSequenceRef.current = latestReadyFocus.sequence;
    focusModuleRef.current(latestReadyFocus.moduleId, { anchorMessageId: latestReadyFocus.anchorMessageId });
  }, [activeConversation?.id, availableModules]);

  return {
    dispatchModuleCommand,
    focusModule,
    focusModuleRef,
    hideModulePane,
  };
}
